import React, { useEffect, useMemo, useState } from 'react';
import { userAPI } from '../../api';

const UserMainToDepositTransfer = () => {
  const [wallet, setWallet] = useState({ mainWallet: 0, depositWallet: 0, repurchaseWallet: 0 });
  const [amount, setAmount] = useState('');
  const [password, setPassword] = useState('');
  const [loadingWallet, setLoadingWallet] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [message, setMessage] = useState(null);

  const parsedAmount = Number(amount);
  const isAmountValid =
    amount !== '' &&
    !Number.isNaN(parsedAmount) &&
    Number.isFinite(parsedAmount) &&
    parsedAmount >= 100 &&
    /^\d+(\.\d{1,2})?$/.test(String(amount));

  const deductions = useMemo(() => {
    const gross = isAmountValid ? parsedAmount : 0;
    const tdsDeduction = Number((gross * 0.05).toFixed(2));
    const repurchaseDeduction = Number((gross * 0.05).toFixed(2));
    const totalDeduction = Number((tdsDeduction + repurchaseDeduction).toFixed(2));
    const netAmount = Number((gross - totalDeduction).toFixed(2));
    return { gross, tdsDeduction, repurchaseDeduction, totalDeduction, netAmount };
  }, [isAmountValid, parsedAmount]);

  const fetchWallet = async () => {
    setLoadingWallet(true);
    try {
      const res = await userAPI.getWallet();
      setWallet({
        mainWallet: res?.data?.wallet?.mainWallet || 0,
        depositWallet: res?.data?.wallet?.depositWallet || 0,
        repurchaseWallet: res?.data?.wallet?.repurchaseWallet || 0,
      });
    } catch (err) {
      setMessage({ type: 'error', text: err.response?.data?.error || 'Failed to load wallet details.' });
    } finally {
      setLoadingWallet(false);
    }
  };

  useEffect(() => {
    fetchWallet();
  }, []);

  const handleAmountChange = (e) => {
    const value = e.target.value;
    if (value === '') {
      setAmount('');
      setMessage(null);
      return;
    }

    if (/^\d*(\.\d{0,2})?$/.test(value)) {
      setAmount(value);
      setMessage(null);
    }
  };

  const validate = () => {
    if (!isAmountValid) return 'Enter a valid transfer amount.';
    if (parsedAmount < 100) return 'Minimum conversion amount is ₹100.';
    if (parsedAmount > wallet.mainWallet) return `Insufficient main wallet balance. Available: ₹${wallet.mainWallet}`;
    if (!password) return 'Enter your login password.';
    return null;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setMessage(null);

    const error = validate();
    if (error) {
      setMessage({ type: 'error', text: error });
      return;
    }

    setSubmitting(true);
    try {
      const res = await userAPI.mainToDepositTransfer({
        amount: parsedAmount,
        password,
      });

      const data = res?.data?.data || {};
      setWallet({
        mainWallet: data.mainWallet ?? wallet.mainWallet,
        depositWallet: data.depositWallet ?? wallet.depositWallet,
        repurchaseWallet: data.repurchaseWallet ?? wallet.repurchaseWallet,
      });
      setAmount('');
      setPassword('');
      setMessage({ type: 'success', text: res?.data?.message || 'Transfer completed successfully.' });
    } catch (err) {
      setMessage({ type: 'error', text: err.response?.data?.error || 'Transfer failed. Please try again.' });
    } finally {
      setSubmitting(false);
    }
  };

  if (loadingWallet) {
    return (
      <div className="flex items-center justify-center h-48">
        <div className="w-8 h-8 border-4 border-[#E4572E] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="max-w-xl mx-auto p-4 sm:p-6">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-[#0F2F3D]">Main to Deposit Transfer</h1>
        <p className="text-sm text-gray-500 mt-1">Transfer funds from main wallet to deposit wallet with 10% deduction. Minimum conversion: ₹100.</p>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-6">
        <div className="bg-gradient-to-r from-[#0F2F3D] to-[#1a4a60] rounded-xl p-4 text-white">
          <p className="text-xs opacity-80">Main Wallet</p>
          <p className="text-2xl font-bold mt-1">₹{wallet.mainWallet.toLocaleString('en-IN')}</p>
        </div>
        <div className="bg-gradient-to-r from-[#E4572E] to-[#ff6a3d] rounded-xl p-4 text-white">
          <p className="text-xs opacity-80">Deposit Wallet</p>
          <p className="text-2xl font-bold mt-1">₹{wallet.depositWallet.toLocaleString('en-IN')}</p>
        </div>
        <div className="bg-gradient-to-r from-[#4f46e5] to-[#7c3aed] rounded-xl p-4 text-white">
          <p className="text-xs opacity-80">Repurchase Wallet</p>
          <p className="text-2xl font-bold mt-1">₹{wallet.repurchaseWallet.toLocaleString('en-IN')}</p>
        </div>
      </div>

      {message && (
        <div
          className={`mb-5 px-4 py-3 rounded-lg text-sm font-medium ${
            message.type === 'success'
              ? 'bg-green-50 border border-green-200 text-green-700'
              : 'bg-red-50 border border-red-200 text-red-700'
          }`}
        >
          {message.type === 'success' ? '✅ ' : '❌ '}
          {message.text}
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-5">
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-5 space-y-3">
          <h2 className="text-base font-semibold text-[#0F2F3D]">Transfer Amount</h2>

          <div className="relative">
            <span className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 font-semibold">₹</span>
            <input
              type="number"
              min="100"
              max={wallet.mainWallet}
              step="0.01"
              value={amount}
              onChange={handleAmountChange}
              placeholder="0"
              className="w-full border border-gray-300 rounded-lg pl-8 pr-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#E4572E]"
            />
          </div>

          <div className="grid grid-cols-2 gap-2 text-sm">
            <div className="bg-gray-50 rounded-lg p-3 flex justify-between">
              <span>TDS (5%)</span>
              <span className="font-semibold">₹{deductions.tdsDeduction.toLocaleString('en-IN')}</span>
            </div>
            <div className="bg-gray-50 rounded-lg p-3 flex justify-between">
              <span>Repurchase (5%)</span>
              <span className="font-semibold">₹{deductions.repurchaseDeduction.toLocaleString('en-IN')}</span>
            </div>
            <div className="bg-yellow-50 rounded-lg p-3 flex justify-between col-span-2">
              <span>Total Deduction (10%)</span>
              <span className="font-semibold">₹{deductions.totalDeduction.toLocaleString('en-IN')}</span>
            </div>
            <div className="bg-green-50 rounded-lg p-3 flex justify-between col-span-2">
              <span>Net Amount to Deposit Wallet</span>
              <span className="font-bold text-green-700">₹{deductions.netAmount.toLocaleString('en-IN')}</span>
            </div>
          </div>

          <div>
            <label className="block mb-2 text-sm font-semibold text-[#0F2F3D]">Password</label>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Enter your login password"
              className="w-full border border-gray-300 rounded-lg px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#E4572E]"
            />
          </div>
        </div>

        <button
          type="submit"
          disabled={submitting || !isAmountValid || parsedAmount > wallet.mainWallet || !password}
          className="w-full bg-[#E4572E] hover:bg-[#c94a26] text-white font-semibold py-3 rounded-xl transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
        >
          {submitting ? 'Transferring...' : 'Transfer to Deposit Wallet'}
        </button>
      </form>
    </div>
  );
};

export default UserMainToDepositTransfer;
